/**
 * <DivisorChip> and <LiveSplitBar> — 13-ui-screens.md §7.9, 14-theme.md §7.6.
 *
 * The safety check on the daily entry screen. It is the ONLY place the divisor
 * appears and it is NEVER an input: the divisor is system-derived (FR-4.7) and
 * `shareAmount` always comes from the server — the UI never divides anything.
 */
import type { ReactNode } from 'react';
import { Money } from './Money';
import type { Money as MoneyValue } from '@/lib/money';

export interface DivisorChipProps {
  /** Work-type name, or `Untyped job` when no work type has been chosen. */
  name: string;
  divisor: number;
  /** Per-head share, from the server. Absent when nobody is ticked. */
  share?: MoneyValue;
}

export function DivisorChip({ name, divisor, share }: DivisorChipProps) {
  const empty = divisor <= 0;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        borderRadius: 4,
        padding: '4px 9px',
        border: empty ? '1px dashed var(--border-default)' : '1px solid var(--green-300)',
        background: empty ? 'var(--surface-subtle)' : 'var(--green-100)',
        opacity: empty ? 0.75 : 1,
      }}
    >
      <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{name}</span>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          fontSize: 14,
          color: empty ? 'var(--neutral-400)' : 'var(--brand-primary)',
        }}
      >
        {`÷ ${divisor}`}
      </span>
      {empty ? (
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>no one ticked</span>
      ) : (
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          <Money value={share} /> ea
        </span>
      )}
    </span>
  );
}

/* ─────────────────────────── the sticky footer ────────────────────────── */

export interface LiveSplitBarProps {
  /** One `<DivisorChip>` per job line. Empty ⇒ the "no jobs yet" copy. */
  chips: ReactNode;
  hasJobs: boolean;
  /** MEALS block (day / night counts), already composed by the screen. ADR-0011 retired the OT pot from it. */
  meals?: ReactNode;
  /** `Σ (quantity × rateApplied) + Σ leaderBonus` — the leader bonus IS in it. */
  dayTotal: MoneyValue;
  /** `Save` + the readiness note, or `Period closed` / `Period locked` — the screen decides (ADR-0012). */
  action?: ReactNode;
}

export function LiveSplitBar({ chips, hasJobs, meals, dayTotal, action }: LiveSplitBarProps) {
  return (
    /* `position: sticky`, NOT antd <Affix>: Affix measures a scroll container
     * and fights `overflow: auto` on <main> (13-ui-screens.md §3.7). */
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 35,
        background: 'var(--surface-card)',
        borderTop: '2px solid var(--brand-primary)',
        boxShadow: '0 -8px 24px rgba(6,52,30,.10)',
        padding: '11px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 800,
          fontSize: 11,
          letterSpacing: '.16em',
          color: 'var(--brand-deep)',
        }}
      >
        LIVE SPLIT
      </span>

      {hasJobs ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{chips}</div>
      ) : (
        <span style={{ color: 'var(--neutral-400)', fontSize: 12 }}>
          Divisors appear as soon as a job is booked. They are derived from ticks — never typed.
        </span>
      )}

      {meals ? (
        <div style={{ paddingLeft: 14, borderLeft: '1px solid var(--border-subtle)' }}>{meals}</div>
      ) : null}

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 10,
              letterSpacing: '.16em',
              color: 'var(--text-muted)',
            }}
          >
            JOB VALUE BOOKED
          </div>
          <Money value={dayTotal} variant="total" />
        </div>
        {action}
      </div>
    </div>
  );
}
